import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Progress } from "@shared/ui/progress";
import {
  BatteryWarning,
  Calendar,
  CalendarCheck,
  CircleDollarSign,
  Coins,
  CreditCard,
  DollarSign,
  FileBox,
  FileCheck,
  FileMinus,
  FilePlus,
  FileText,
  FileTextIcon,
  PercentCircle,
  Printer,
  Receipt,
  ReceiptTextIcon,
  TrendingDown,
  TrendingUp,
  TriangleAlert,
  Wallet,
  Zap,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  useBillCycleList,
  useDashboardData,
  useSummaryData,
} from "../../hooks";
import KpiCard from "./KpiCard";
import { QuickActions } from "@shared/components/QuickActions";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { useNavigate } from "react-router-dom";
import { QuickActionItem } from "@shared/components/QuickActions";
import { logEvent } from "@amplitude/analytics-browser";
import { navigateTo } from "@shared/utils/navigation";
const Landing = () => {
  const today = new Date().toISOString().split("T")[0]; // '2025-06-17'
  const { data: dashboardData } = useDashboardData(today);
  const { data: billCycleList } = useBillCycleList();

  const [selectedCycleId, setSelectedCycleId] = useState<string>("");

  const { data: summaryData } = useSummaryData(selectedCycleId);

  const handleBillCycleSelection = (cycleId: string) => {
    setSelectedCycleId(cycleId);
  };

  const navigate = useNavigate();

  const quickActions: QuickActionItem[] = [
    {
      title: "Bulk Bill Generation",
      description: "Generate bills for multiple consumers",
      icon: FilePlus,
      color:
        "bg-blue-50 hover:bg-blue-100 text-blue-800 border border-blue-200",
      onClick: () => {
        logEvent("BX Quick Action Used - Bulk Bill Generation");
        navigateTo(navigate, "bx", "create-bill-generation");
      },
    },
    {
      title: "Single Bill Generation",
      description: "Generate bill for a single consumer",
      icon: FileTextIcon,
      color:
        "bg-green-50 hover:bg-green-100 text-green-800 border border-green-200",
      onClick: () => {
        logEvent("BX Quick Action Used - Single Bill Generation");
        navigateTo(navigate, "bx", "single-bill-generation");
      },
    },
    {
      title: "Print & Distribute",
      description: "Manage printing and distribution",
      icon: Printer,
      color:
        "bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-200",
      onClick: () => {
        logEvent("BX Quick Action Used - Print & Distribute");
        navigateTo(navigate, "bx", "printing-distribution");
      },
    },
    {
      title: "GL Codes Management",
      description: "Configure and manage GL codes",
      icon: FileMinus,
      color:
        "bg-purple-50 hover:bg-purple-100 text-purple-800 border border-purple-200",
      onClick: () => {
        logEvent("BX Quick Action Used - GL Codes Management");
        navigateTo(navigate, "bx", "gl-code");
      },
    },
  ];

  useEffect(() => {
    if (billCycleList && billCycleList.length > 0 && !selectedCycleId) {
      setSelectedCycleId(billCycleList[0].id);
    }
  }, [billCycleList]);

  return (
    <div className="">
      <div className="mb-4">
        <h1 className="font-heading text-2xl font-semibold">
          Billing Dashboard
        </h1>
        <p className="text-sm text-muted-foreground">
          Monitor billing cycles, review exceptions, and track revenue
        </p>
      </div>

      {/* Monthly Performance Section */}
      <div className="mb-5 bg-card rounded-lg p-4 border shadow-sm">
        <h2 className="page-subheading mb-3 flex items-center">
          <Calendar className="mr-2 h-5 w-5 text-blue-500" />
          Monthly Performance
        </h2>

        {/* Billing Summary Section */}
        <div className="mb-5">
          <h3 className="card-heading-text mb-3 flex items-center">
            Billing Summary
          </h3>
          <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
            <QuickStatsCard
              title="Total Bills Generated"
              count={dashboardData?.processedBills.current ?? 0}
              icon={<FileText className="text-green-500" />}
              trend={dashboardData?.processedBills.percentageChange ?? 0}
              trendDurationDisplay="vs. last period"
              tooltip="Total number of bills generated in the current month"
            />

            <QuickStatsCard
              title="Bills Not Generated"
              count={dashboardData?.failedBills.current ?? 0}
              trend={dashboardData?.failedBills.percentageChange ?? 0}
              icon={<TriangleAlert className="text-destructive" />}
              trendDurationDisplay="vs. last period"
              tooltip="Number of bills that failed to generate"
            />

            <QuickStatsCard
              title="Success Rate"
              count={dashboardData?.successRate.current ?? 0}
              icon={<PercentCircle className="text-blue-500" />}
              trendDurationDisplay="vs. last period"
              tooltip="Percentage of bills successfully generated"
            />

            <QuickStatsCard
              title="Average Bill Amount"
              count={dashboardData?.averageBillAmount.current ?? 0}
              icon={<DollarSign className="text-amber-500" />}
              tooltip="Average amount per bill generated"
            />
          </div>
        </div>

        {/* Consumption Distribution */}
        <div className="mb-5">
          <h3 className="card-heading-text mb-3">Consumption Distribution</h3>
          <div className="grid gap-3 grid-cols-1 md:grid-cols-4">
            <QuickStatsCard
              title="Total Consumption"
              count={dashboardData?.totalConsumption.current ?? 0}
              icon={<Zap className="" />}
              trend={dashboardData?.totalConsumption.percentageChange ?? 0}
              trendDurationDisplay="vs. last period"
              tooltip="Total consumption for the billing period"
            />

            <QuickStatsCard
              title="High Consumption"
              count={dashboardData?.highConsumption.current ?? 0}
              icon={<TrendingUp className="text-secondary" />}
              trend={dashboardData?.highConsumption.percentageChange ?? 0}
              trendDurationDisplay="vs. last period"
              tooltip="Consumers with unusually high consumption"
            />

            <QuickStatsCard
              title="Low Consumption"
              count={dashboardData?.lowConsumption.current ?? 0}
              icon={<TrendingDown className="text-primary" />}
              trend={dashboardData?.lowConsumption.percentageChange ?? 0}
              tooltip="Consumers with unusually low consumption"
            />

            <QuickStatsCard
              title="Zero Consumption"
              count={dashboardData?.zeroConsumption.current ?? 0}
              icon={<BatteryWarning className="text-destructive" />}
              trend={dashboardData?.zeroConsumption.percentageChange}
              trendDurationDisplay="vs. last period"
              tooltip="Consumers with no recorded consumption"
            />
          </div>
        </div>

        {/* Financial Summary */}
        <div className="mb-5">
          <h3 className="text-md font-medium mb-3 flex items-center">
            <CircleDollarSign className="mr-2 text-primary" />
            Financial Summary
          </h3>
          <div className="grid gap-3 grid-cols-1 md:grid-cols-4">
            <QuickStatsCard
              title="Total Billed Amount"
              count={dashboardData?.totalBillAmount.current ?? 0}
              trendDurationDisplay="vs. last period"
              icon={<Receipt className="" />}
              trend={dashboardData?.totalBillAmount.percentageChange ?? 0}
              tooltip="Total amount billed to consumers"
            />

            <QuickStatsCard
              title="Service Charges"
              count={dashboardData?.serviceCharges.current ?? 0}
              icon={<CreditCard className="" />}
              trend={dashboardData?.serviceCharges.percentageChange ?? 0}
              tooltip="Total service charges collected"
            />

            <QuickStatsCard
              title="Outstanding Amount"
              count={dashboardData?.outstandingAmount.current ?? 0}
              icon={<Wallet className="" />}
              trend={dashboardData?.outstandingAmount.percentageChange ?? 0}
              tooltip="Total unpaid amount from consumers"
            />

            <QuickStatsCard
              title="Late Payment Revenue"
              count={dashboardData?.latePaymentRevenue.current ?? 0}
              icon={<Coins className="" />}
              tooltip="Revenue from late payment fees"
            />
          </div>
        </div>

        {/* Bill Types */}
      </div>

      {/* Active Billing Cycles Section */}
      <div className="mb-5 bg-card rounded-lg p-4 border shadow-sm">
        <h2 className="text-lg font-semibold mb-3 flex items-center">
          <CalendarCheck className="mr-2 h-5 w-5 text-primary" />
          Active Billing Cycles
        </h2>

        <div className="mb-4">
          <select
            value={selectedCycleId}
            onChange={(e) => handleBillCycleSelection(e.target.value)}
            className="w-full sm:w-[350px]"
          >
            <option value="">{"Select a billing cycle"}</option>
            {billCycleList?.map((cycle: any) => (
              <option key={cycle.id} value={cycle.id}>
                {cycle.name}
              </option>
            ))}
          </select>
        </div>

        <div className="p-3 rounded-lg mb-4">
          <div className="grid gap-3 grid-cols-2 md:grid-cols-4 my-3">
            <QuickStatsCard
              title="Total Bills"
              count={summaryData?.totalBills ?? 0}
              icon={<FileText className="" />}
            />

            <QuickStatsCard
              title="Bills Not Generated"
              count={summaryData?.failedBills ?? 0}
              icon={<TriangleAlert className="text-destructive" />}
            />

            <QuickStatsCard
              title="Success Rate"
              count={`${summaryData?.billSuccessPercentage}%`}
              icon={<PercentCircle className="" />}
            />

            <QuickStatsCard
              title="Average Bill Amount"
              count={`${summaryData?.avgTotalBillAmount ?? 0}`}
              icon={<DollarSign className="" />}
            />
          </div>

          <div className="grid gap-4 grid-cols-1 md:grid-cols-3 mt-3">
            <div className="md:col-span-1">
              {/* <Card className="h-full ">
                <CardHeader className="p-3 pb-1">
                  <CardTitle className="text-md font-medium">
                    Bill Types
                  </CardTitle>
                  <CardDescription>Actual vs Estimated</CardDescription>
                </CardHeader>
                <CardContent className="p-3 pt-1">
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between mb-1 text-sm">
                        <div className="flex items-center">
                          <FileCheck className="mr-1 text-primary" />
                          <span>Actual Bills</span>
                        </div>
                        <span>{summaryData?.percentageActualBills}%</span>
                      </div>
                      <Progress
                        value={summaryData?.percentageActualBills}
                        className="h-2"
                      />
                      <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                        <span>This: {summaryData?.actualBills}</span>
                        <span>Last: {summaryData?.previousActualBills}</span>
                      </div>
                    </div>

                    <div>
                      <div className="flex justify-between mb-1 text-sm">
                        <div className="flex items-center">
                          <FileBox className="mr-1 text-cyan-500" />
                          <span>Estimated Bills</span>
                        </div>
                        <span>{summaryData?.percentageNonActualBills}%</span>
                      </div>
                      <Progress
                        value={summaryData?.percentageNonActualBills}
                        className="h-2"
                      />
                      <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                        <span>This: {summaryData?.nonActualBills}</span>
                        <span>Last: {summaryData?.previousNonActualBills}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card> */}
            </div>
          </div>

          <div className="grid gap-4 grid-cols-1 md:grid-cols-3 mt-3">
            <Card className="h-full ">
              <CardHeader className="">
                <CardTitle className="text-md font-medium flex items-center gap-2 pb-4">
                  <ReceiptTextIcon className="text-primary" />
                  Bill Types
                </CardTitle>
                <CardDescription className="card-subheading-text">
                  Actual vs Estimated
                </CardDescription>
              </CardHeader>
              <CardContent className="p-3 pt-1">
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between mb-1 text-sm">
                      <div className="flex items-center">
                        <FileCheck className="mr-1 h-4 w-4" />
                        <span>Actual Bills</span>
                      </div>
                      <span>{summaryData?.percentageActualBills}%</span>
                    </div>
                    <Progress
                      value={summaryData?.percentageActualBills}
                      className="h-2"
                    />
                    <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                      <span>This: {summaryData?.actualBills}</span>
                      <span>Last: {summaryData?.previousActualBills}</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between mb-1 text-sm">
                      <div className="flex items-center">
                        <FileBox className="mr-1 text-cyan-500 h-4 w-4" />
                        <span>Estimated Bills</span>
                      </div>
                      <span>{summaryData?.percentageNonActualBills}%</span>
                    </div>
                    <Progress
                      value={summaryData?.percentageNonActualBills}
                      className="h-2"
                    />
                    <div className="flex justify-between mt-1 text-xs text-muted-foreground">
                      <span>This: {summaryData?.nonActualBills}</span>
                      <span>Last: {summaryData?.previousNonActualBills}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            <div className="">
              {/* Consumption metrics for selected cycle */}
              <Card className="h-full ">
                <CardHeader className="pb-4">
                  <CardTitle className="text-md font-medium flex items-center">
                    <Zap className="mr-2 text-primary" />
                    Consumption Metrics
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-2">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="p-2 border rounded-md">
                      <div className="text-xs font-medium mb-1 flex items-center">
                        <TrendingUp className="h-3 w-3 mr-1 text-green-500" />{" "}
                        High
                      </div>
                      <div className="text-lg font-bold">
                        {summaryData?.highConsumptionBills}
                      </div>
                      <div className="text-xs flex justify-between">
                        <span className="text-muted-foreground">
                          Last: {summaryData?.previousHighConsumptionBills}
                        </span>
                      </div>
                    </div>

                    <div className="p-2 border rounded-md">
                      <div className="text-xs font-medium mb-1 flex items-center">
                        <TrendingDown className="h-3 w-3 mr-1 text-primary" />{" "}
                        Low
                      </div>
                      <div className="text-lg font-bold">
                        {summaryData?.lowConsumptionBills}
                      </div>
                      <div className="text-xs flex justify-between">
                        <span className="text-muted-foreground">
                          Last: {summaryData?.previousLowConsumptionBills}
                        </span>
                      </div>
                    </div>

                    <div className="p-2 border rounded-md">
                      <div className="text-xs font-medium mb-1 flex items-center">
                        <BatteryWarning className="h-3 w-3 mr-1 text-destructive" />{" "}
                        Zero
                      </div>
                      <div className="text-lg font-bold">
                        {summaryData?.zeroConsumptionBills}
                      </div>
                      <div className="text-xs flex justify-between">
                        <span className="text-muted-foreground">
                          Last: {summaryData?.previousZeroConsumptionBills}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
            <div className="md:col-span-1">
              {/* Financial summary for selected cycle */}
              <Card className="h-full ">
                <CardHeader className="pb-4">
                  <CardTitle className="text-md font-medium flex items-center">
                    <CircleDollarSign className="mr-2 text-primary" />
                    Financial Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center border-b pb-1">
                      <span className="text-xs">Total Billed</span>
                      <span className="card-value-text">
                        {summaryData?.totalBillAmount}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b pb-1">
                      <span className="text-xs">Service Charges</span>
                      <span className="card-value-text">
                        {summaryData?.totalServiceCharges}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-xs">Outstanding</span>
                      <span className="card-value-text">
                        {summaryData?.totalOutstanding}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        <div>
          <div>
            <QuickActions actions={quickActions} />
          </div>
        </div>
      </div>
    </div>
  );
};
export default Landing;
